/**
 * What: The "What would you like to test?" two-pane body: the field list on the left, the picked field's value, its
 *   "Test this field as" choice and the shopper-question cards on the right. Drawn for the Worker's `field-picker` block.
 * Transport / data-flow: Dumb renderer over the block. It holds only which row is viewed (local UI, like a tab or
 *   Show-more); every word, value and concept comes from the block. Each concept choice carries its finished `comparisons.add`
 *   edit; picking one reports that edit verbatim, which creates or updates this test's one comparison.
 */
import { useState } from 'react'
import type { BuilderBlock } from '@testfeed/shopify-contracts'
import { ReviewFieldDetail } from './ReviewFieldDetail'
import { ReviewFieldsList } from './ReviewFieldsList'
import type { OnEdit } from './SectionBlocks'
import { useAppBridge } from '@shopify/app-bridge-react'
import { trackShopifyAction } from '../../shopifyUiTracking'
import './ReviewFieldsPane.css'

type FieldPickerBlock = Extract<BuilderBlock, { kind: 'field-picker' }>

export function ReviewFieldsPane({
  block,
  onEdit,
  disabled,
}: {
  block: FieldPickerBlock
  onEdit: OnEdit
  disabled: boolean
}) {
  const shopify = useAppBridge()
  const [viewedId, setViewedId] = useState(block.selectedId)
  // When the server moves its default (a new comparison, a re-read), follow it — adjust state during render, no effect.
  const [lastDefault, setLastDefault] = useState(block.selectedId)
  if (lastDefault !== block.selectedId) {
    setLastDefault(block.selectedId)
    setViewedId(block.selectedId)
  }
  // The viewed row is the merchant's local choice; if it is gone, fall back to the Worker's default (superRefine guarantees it).
  const selected =
    block.rows.find((row) => row.id === viewedId) ?? block.rows.find((row) => row.id === block.selectedId)!
  return (
    <div className="tf-review-fields">
      <div className="tf-review-fields__pane">
        <ReviewFieldsList
          block={block}
          selectedId={selected.id}
          onSelect={(id) => trackShopifyAction(shopify, 'select-field', () => setViewedId(id))}
          disabled={disabled}
        />
      </div>
      <div className="tf-review-fields__pane">
        <ReviewFieldDetail
          block={block}
          row={selected}
          disabled={disabled}
          onChoose={(edit) => onEdit(edit.key, edit.value)}
        />
      </div>
    </div>
  )
}
